import {
  Alert,
  Button,
  Grid,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form1042SInstructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Instructions for Form 1042-S | Foreign Income Reporting 2025"
          description="E-file IRS Form 1042-S to report U.S.-source income paid to foreign persons. Step-by-step instructions ensure fast, IRS-compliant filing in 2025."
          keywords=""
          canonical="resources/information-returns/form-1042-s-instructions/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          IRS{' '}
          <span className="highlight-container">
            <span className="highlight">Form 1042-S</span>
          </span>{' '}
          Instructions: Complete Guide{' '}
          <Hidden smDown>
            {' '}
            <br />
          </Hidden>{' '}
          for Withholding Agents
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          E-file IRS Form 1042-S to report U.S.-source income paid to foreign
          persons and stay compliant.{' '}
          <Hidden smDown>
            {' '}
            <br />
          </Hidden>{' '}
          Follow our simple step-by-step instructions for accurate, hassle-free
          filing.
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1042sinstruction`}
          >
            E-file Form 1042-S Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/form-1042-s-instructions/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-07T09:00:00-05:00"
                      >
                        July 7, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-07T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 7, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 1042-S at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>Form 1042-S</strong>
                        {
                          ' reports U.S.-source income paid to foreign persons, including nonresident aliens, foreign entities, and other foreign recipients, along with any federal tax withheld. Withholding agents use this form to report payments subject to Chapter 3 or Chapter 4 withholding and certain income exempt under an applicable tax treaty.'
                        }
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        {'Recipient copies must be furnished by '}
                        <strong style={{ fontWeight: 600 }}>March 15</strong>
                        {
                          ' following the calendar year being reported. The IRS filing deadline is '
                        }
                        <strong style={{ fontWeight: 600 }}>March 15</strong>
                        {' for both paper and electronic filings.'}
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        {
                          'This article covers line-by-line instructions, including withholding agent and recipient information, '
                        }
                        <strong style={{ fontWeight: 600 }}>{'Box 1 '}</strong>
                        (Income Code)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 2 '}</strong>
                        (Gross Income)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 3 '}</strong>
                        (Chapter Indicator)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>Box 4</strong>
                        {' (Exemption Code)'}
                        {', '}
                        <strong style={{ fontWeight: 600 }}>Box 5</strong>
                        {' (Withholding Allowance)'}
                        {', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 6 '}</strong>
                        (Net Income)
                        {','}{' '}
                        <strong style={{ fontWeight: 600 }}>{'Box 7 '}</strong>
                        (Federal Tax Withheld)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>Box 8</strong>
                        {' (Tax Rate)'}
                        {', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 9 '}</strong>
                        (Withholding Agent's EIN)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 10 '}</strong>
                        (Recipient's U.S. TIN)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 11 '}</strong>
                        (Recipient's Foreign TIN)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 12 '}</strong>
                        (Withholding Agent's Details){', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 13 '}</strong>
                        (Recipient
                        {' Details'}){', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 14 '}</strong>
                        (Primary Withholding Agents Name and EIN)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 15 '}</strong>
                        (Pro-Rata Basis Reporting Checkbox)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>Box 16</strong>
                        {' (Payer Information)'}
                        {', and '}
                        <strong style={{ fontWeight: 600 }}>{'Box 17 '}</strong>
                        (State Income Tax Withheld and Related State Reporting
                        Information) .
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>Penalties:</strong>
                        {' Late, incorrect, or incomplete filing of '}
                        <strong style={{ fontWeight: 600 }}>Form 1042-S</strong>
                        {' may result in IRS penalties ranging from '}
                        <strong style={{ fontWeight: 600 }}>
                          $60 to $680 per return
                        </strong>
                        , depending on the delay, correction requirements, and
                        whether the filer meets electronic filing obligations.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="5" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>Extensions:</strong>{' '}
                        <Link
                          href="/tax-extension/efile-irs-form-8809-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          <strong style={{ fontWeight: 600 }}>
                            File Form 8809
                          </strong>
                        </Link>
                        {' to request an automatic filing extension. '}
                        <Link
                          href="/tax-extension/file-irs-form-15397-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          <strong style={{ fontWeight: 600 }}>
                            File Form 15397
                          </strong>
                        </Link>{' '}
                        {'to request the full '}
                        <strong style={{ fontWeight: 600 }}>
                          30-day extension
                        </strong>
                        {' for furnishing recipient copies'}
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  IRS Form 1042-S, titled “Foreign Person’s U.S. Source Income
                  Subject to Withholding,” is a critical document that
                  withholding agents use to report payments of U.S.-source
                  income to foreign individuals or entities and any federal
                  income tax withheld. This form ensures transparency and
                  enforcement of U.S. tax laws affecting nonresident aliens,
                  foreign corporations, partnerships, trusts, estates, and other
                  foreign recipients.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  The form must be submitted annually to the IRS and copies
                  provided to recipients to assist them in complying with tax
                  obligations in their respective countries.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who Must File Form 1042-S?
                </Typography>
                <Typography variant="body1">
                  Any person or entity that acts as a withholding agent and
                  makes payments from U.S. sources to foreign persons is
                  required to{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1042-s/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    file Form 1042-S
                  </Link>
                  . This obligation spans:
                </Typography>

                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>U.S. and foreign corporations or businesses</li>
                  <li>Financial institutions such as banks and brokerages</li>
                  <li>Educational and research institutions</li>
                  <li>U.S. government agencies and state entities </li>
                  <li>Nonprofit organizations </li>
                  <li>Individuals acting as withholding agents </li>
                  <li>
                    Qualified intermediaries, withholding foreign partnerships,
                    and trusts{' '}
                  </li>
                  <li>
                    Flow-through entities that pass through income to foreign
                    recipients{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Each unique recipient and each type of income paid requires a
                  separate Form 1042-S, ensuring detailed and specific
                  reporting.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Detailed Income Types Reported on Form 1042-S
                </Typography>
                <Typography variant="body1">
                  Form 1042-S comprehensively reports diverse categories of
                  income, including:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Interest and Dividends:
                    </span>{' '}
                    Payments from U.S. corporations, foreign corporations with
                    U.S. activities, and financial instruments.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Royalties:</span> Income
                    from copyrights, patents, trademarks, licenses, software,
                    and broadcasting rights.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Scholarships and Fellowships:
                    </span>{' '}
                    Grants provided to foreign students and researchers.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Compensation:</span>{' '}
                    Earnings from personal services, including both independent
                    contractors and employees.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Rents and Real Estate Income:
                    </span>{' '}
                    Payments related to U.S. property rentals or leases.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Broker Proceeds:</span>{' '}
                    Income from the sale or exchange of securities handled by
                    brokers.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Prizes and Awards:</span>{' '}
                    Monetary prizes or grants awarded to foreign individuals or
                    entities.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Gambling Winnings:</span>{' '}
                    Reported under specific withholding rules.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Pensions, Annuities, Insurance Premiums:
                    </span>{' '}
                    Periodic payments subject to withholding.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Capital Gains:</span>{' '}
                    Gains from the sale of U.S. property that are subject to
                    withholding.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Other Fixed or Determinable Income:
                    </span>{' '}
                    Includes settlement payments, consent fees, loan syndication
                    fees, etc.{' '}
                  </li>
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{
                    mb: 4,
                    textAlign: 'left',
                    borderLeft: '3px solid rgb(102, 60, 0)',
                  }}
                >
                  <b>Note:</b> Both taxable and exempt income types must be
                  reported, even if withholding tax is not applied or
                  withholding rate is zero.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Step-by-Step Instructions: Completing Form 1042-S
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    fontSize: { md: 20, sm: 18, xs: 18 },
                    mt: 2,
                    mb: 0.5,
                  }}
                >
                  Box 1: Income Code{' '}
                </Typography>
                <Typography variant="body1">
                  Use the IRS-assigned numeric code that corresponds to the
                  income type paid, e.g.,
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>01 for general interest payments, </li>
                  <li>06 for dividends from U.S. corporations, </li>
                  <li>16 for scholarships and fellowships,</li>
                  <li>28 for gambling winnings, </li>
                  <li>
                    59–61 for newer income types like consent fees, loan
                    syndication fees, and settlement payments respectively.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Choose the correct code from the IRS’s official list to avoid
                  rejection.{' '}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    fontSize: { md: 20, sm: 18, xs: 18 },
                    mt: 2,
                    mb: 0.5,
                  }}
                >
                  Box 2: Gross Income
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Report the total gross amount of U.S.-source income paid to
                  the recipient during the tax year before any deductions or
                  withholding.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    fontSize: { md: 20, sm: 18, xs: 18 },
                    mt: 2,
                    mb: 0.5,
                  }}
                >
                  Box 3: Chapter Indicator
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter “3” for Chapter 3 withholding income or “4” for Chapter
                  4 withholding related to FATCA regulations.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    fontSize: { md: 20, sm: 18, xs: 18 },
                    mt: 2,
                    mb: 0.5,
                  }}
                >
                  Boxes 3a and 4a: Exemption Codes
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter applicable exemption codes if the income is exempt from
                  withholding or subject to reduced withholding due to:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Tax treaties, </li>
                  <li>Portfolio interest exemptions, </li>
                  <li>Effective connection with a trade or business, </li>
                  <li>Qualified intermediary status, </li>
                  <li>FATCA-related exemptions. </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    fontSize: { md: 20, sm: 18, xs: 18 },
                    mt: 2,
                    mb: 0.5,
                  }}
                >
                  Boxes 3b and 4b: Tax Rate
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Record the actual withholding tax rate applied to the payment,
                  such as 30%, 0% (exempt), or another treaty-reduced rate.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    fontSize: { md: 20, sm: 18, xs: 18 },
                    mt: 2,
                    mb: 0.5,
                  }}
                >
                  Box 5: Withholding Allowance
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Report any withholding allowances that reduce the taxable
                  amount of income subject to withholding.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    fontSize: { md: 20, sm: 18, xs: 18 },
                    mt: 2,
                    mb: 0.5,
                  }}
                >
                  Box 6: Net Income
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Calculate as the gross income minus withholding allowance.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    fontSize: { md: 20, sm: 18, xs: 18 },
                    mt: 2,
                    mb: 0.5,
                  }}
                >
                  Boxes 7a to 11: Tax Withheld and Related Information
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>7a:</span> Enter total
                    federal tax withheld on income.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>7b:</span> Check if
                    federal tax was withheld but not deposited due to escrow
                    procedures.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>7c:</span> Check if
                    withholding occurred in a subsequent year related to
                    partnership interests.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>7d:</span> Indicates if
                    you are a qualified intermediary or withholding foreign
                    partnership revising reporting details.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>8:</span> Taxes withheld
                    by other withholding agents.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> 9:</span> Report repaid
                    overwithholding amounts to recipients.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>10:</span> Total
                    withholding credit combining tax withheld and
                    adjustments.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> 11:</span> Tax paid by
                    withholding agent that was not withheld from payment.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    fontSize: { md: 20, sm: 18, xs: 18 },
                    mt: 2,
                    mb: 0.5,
                  }}
                >
                  Boxes 12a to 17c: Identification & Status Information
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Include detailed, precise data about all involved parties:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>12a–12i:</span>{' '}
                    Withholding agent’s EIN, chapter 3/4 status, name, GIIN,
                    country/federal tax IDs, and address.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>13a–13l:</span>{' '}
                    Recipient’s name, country code, address, U.S. TIN, status
                    codes, GIIN, foreign tax ID, limitation on benefits (LOB)
                    code (to indicate treaty eligibility), account number, and
                    date of birth.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>14a–14b:</span> Primary
                    withholding agent’s information, if different.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>15a–15i:</span>{' '}
                    Information for intermediaries or flow-through entities
                    handling the payment.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>16a–16e:</span> Payer’s
                    name, TIN, GIIN, and status codes.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> 17a–17c:</span> State
                    income tax withheld, state tax number, and state name (if
                    applicable).{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Use IRS’s official status codes for Chapter 3 and Chapter 4
                  entities precisely to ensure correct classification.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Filing Deadlines and Extensions for 2025 Tax Year
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}> Recipient Copies:</span>{' '}
                    Must be furnished to recipients by{' '}
                    <span style={{ fontWeight: 600 }}>March 15, 2026</span>
                    .{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      IRS Filing Deadlines:
                    </span>{' '}
                    Both paper and electronic filings are due by{' '}
                    <span style={{ fontWeight: 600 }}>
                      March 15, 2026 (or March 17, 2026, if March 15 falls on a
                      weekend or public holiday)
                    </span>
                    .{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Form 1042-S Annual Return:
                    </span>{' '}
                    To be filed by{' '}
                    <span style={{ fontWeight: 600 }}>March 17, 2026</span>
                    .{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Extension:</span> Submit{' '}
                  <Link
                    href="/tax-extension/efile-irs-form-8809-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 8809
                  </Link>{' '}
                  on or before March 15 to obtain a 30-day automatic extension
                  for IRS filing; this does not extend the deadline to furnish
                  recipient copies.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> If the deadline falls on the weekend or federal
                  holidays, it is extended to next business days.
                </Alert>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Penalties for Noncompliance
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Failure to comply with Form 1042-S filing and furnishing
                  requirements can result in significant penalties:
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Late Filing:</li>
                  <ul>
                    <li>
                      <span style={{ fontWeight: 600 }}> $60</span> per form if
                      filed within 30 days after deadline{' '}
                    </li>
                    <li>
                      <span style={{ fontWeight: 600 }}>$130</span> per form if
                      filed after 30 days but by August 1{' '}
                    </li>
                    <li>
                      <span style={{ fontWeight: 600 }}>$340</span> per form if
                      filed after August 1 or not filed at all{' '}
                    </li>
                  </ul>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Incorrect or Incomplete Forms:
                    </span>{' '}
                    Subject to same tiered penalties.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Failure to Furnish Recipient Copies:
                    </span>{' '}
                    Tiered penalties separate from filing penalties.{' '}
                  </li>
                  <li>Intentional Disregard: </li>
                  <ul>
                    <li style={{ fontWeight: 600 }}>
                      Minimum penalty of $680 per form or 10% of amount required
                      to be reported, with no maximum limit.{' '}
                    </li>
                  </ul>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The maximum aggregate penalties can be substantial for
                  high-volume filers.{' '}
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Advantages of Electronic Filing
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Authorized IRS e-file providers like TaxZerone enable
                    streamlined, secure filing.{' '}
                  </li>
                  <li>
                    Support for bulk filings with spreadsheet uploads,
                    minimizing manual entry.{' '}
                  </li>
                  <li>Real-time IRS submission status tracking. </li>
                  <li>Built-in validations reduce rejections and errors. </li>
                  <li>
                    Secure, instant delivery of recipient copies electronically
                    or by mail.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  Frequently Asked Questions (FAQs)
                </Typography>

                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  1. What is the difference between Forms 1042 and 1042-S?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form 1042-S reports withholding on a per-recipient basis,
                  while Form 1042 is an annual summary return for the
                  withholding agent.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  2. What information is needed to e-file Form 1042-S?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Withholding agent details, recipient info, income and
                  exemption codes, withholding tax amounts, chapter 3/4 status,
                  and any corrections.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  3. Are treaty benefits reported on Form 1042-S?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Yes, treaty country, article, exemption code, and reduced
                  withholding rate must be properly reported.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  4. Is filing required if no tax was withheld?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Yes, filing is mandatory even if withholding is zero, provided
                  the income is U.S.-source and reportable.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  5. Can multiple Forms 1042-S be filed electronically?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Yes, bulk filing processes simplify submission of large
                  quantities of forms.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 2,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Make your Form 1042-S filing fast, secure, and{' '}
              <Hidden smDown>
                {' '}
                <br />
              </Hidden>{' '}
              error-free with TaxZerone.
            </Typography>
            {/* <Typography
              variant="h3"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
                lineHeight: 1.4,
              }}
            >
              E-Filing starts at{' '}
              <span style={{ fontWeight: 600 }}>$ 2.49 per</span> form and can
              go as low as{' '}
              <span style={{ fontWeight: 600 }}>$0.59 Per form</span>
            </Typography> */}

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1042sinstructions`}
              >
                Start Filing Form 1042-S
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form1042SInstructions;
